import { c as _c } from "react/compiler-runtime";
// Панели кабинета, перенесённые из прежней страницы-вкладок profile.tsx при
// переходе на макет «Профиль» (сайдбар + разделы). Используются разделами
// /profile/* до их перевёрстки 1:1; то, что уже свёрстано по макету, сюда не
// возвращается.
import { useQuery } from "@tanstack/react-query";
import { ArrowDownLeft, ArrowUpRight, Building2, Eye, LogOut, MapPin, MessageCircle, Pencil, Send, Smartphone, Star, UserRound, Wallet } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { orderStatusLabel } from "@osmix/contracts/order-status";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { toast } from "sonner";
import { api, type BillingProfile, type BuyerAddress, type BuyerBalance, type BuyerSession, type ChatConversation, type LedgerEntry, type Order } from "@/api";
import { AddressFormDialog, addressLine, buyerAddressInputFromValues, formatAddressPhone, type BuyerAddressFormValues } from "@/components/buyer/address-form";
import { BillingProfileForm } from "@/components/buyer/billing-profile-form";
import { orderNumber } from "@/components/profile/order-view";
import { EmptyState, ErrorState } from "@/components/common/states";
import { InvoiceView } from "@/components/orders/invoice-view";
import { OrderReceipt } from "@/components/orders/order-receipt";
import { ReturnRequestDialog } from "@/components/orders/return-request-dialog";
import { Button } from "@osmix/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@osmix/ui/dialog";
import { Input } from "@osmix/ui/input";
import { Skeleton } from "@osmix/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { findOwnedNotificationEntity } from "@/lib/notification-action";
import { useFormatPrice } from "@/lib/use-format-price";
import { cn } from "@/lib/utils";
import { useCart, useWishlist } from "@/store";
export function Panel(t0) {
  const $ = _c(13);
  const {
    title,
    hint,
    loading,
    children
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <h2 className="type-h2">{title}</h2>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== hint) {
    t2 = <p className="type-body2 text-osmix-text-2">{hint}</p>;
    $[2] = hint;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div>{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== children || $[8] !== loading) {
    t4 = loading ? <Skeleton className="h-40 rounded-2xl" /> : children;
    $[7] = children;
    $[8] = loading;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <section className="flex flex-col gap-4">{t3}{t4}</section>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}
export function AddressesPanel({
  addresses,
  contact,
  onChanged,
  loading
}: {
  addresses: BuyerAddress[];
  contact: {
    recipient: string;
    phone: string;
  };
  onChanged: () => Promise<void>;
  loading: boolean;
}) {
  const {
    t
  } = useTranslation();
  const [editingAddress, setEditingAddress] = useState<BuyerAddress | null>(null);
  const [creating, setCreating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const saveAddress = async (values: BuyerAddressFormValues) => {
    setSubmitting(true);
    try {
      if (editingAddress) {
        await api.buyer.updateAddress(editingAddress.id, buyerAddressInputFromValues(values));
        toast.success(t("profile.addressUpdated"));
      } else {
        await api.buyer.createAddress({
          ...buyerAddressInputFromValues(values),
          isDefault: values.isDefault || addresses.length === 0
        });
        toast.success(t("profile.addressSaved"));
      }
      await onChanged();
      setCreating(false);
      setEditingAddress(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };
  return <Panel title={t("profile.addresses")} hint={t("profile.addressesHint")} loading={loading}>
			<div className="flex flex-col gap-4">
				<Button variant="osmix-filled" size="pill" className="self-start" onClick={() => setCreating(true)}>{t("profile.addAddress")}</Button>
				<div className="grid gap-3">
					{addresses.length === 0 ? <EmptyState icon={MapPin} title={t("profile.noAddresses")} hint={t("profile.noAddressesHint")} /> : addresses.map(address => <article key={address.id} className="flex items-start justify-between gap-4 rounded-2xl bg-card p-4 shadow-block">
							<div className="min-w-0">
								<p className="type-body1-bold">{address.label || address.recipient}</p>
								<p className="type-body2 text-osmix-text-2">{address.country}, {addressLine(address)}</p>
								<p className="type-small text-osmix-icon">{address.recipient} · {formatAddressPhone(address.phone)}</p>
								{address.isDefault && <span className="mt-2 inline-flex rounded-full bg-status-published/10 px-3 py-1 type-small text-status-published">{t("profile.default")}</span>}
							</div>
							<Button type="button" variant="osmix-outline" size="pill-sm" onClick={() => setEditingAddress(address)}>
								<Pencil className="mr-1 size-4" />
								{t("profile.edit")}
							</Button>
						</article>)}
				</div>
			</div>
			<AddressFormDialog open={creating || Boolean(editingAddress)} onOpenChange={open => {
      if (!open) {
        setCreating(false);
        setEditingAddress(null);
      }
    }} address={editingAddress} contact={contact} onSubmit={saveAddress} submitting={submitting} />
		</Panel>;
}

// Прежняя панель «Способы оплаты» с формой ввода номера карты снята
// (fd-payment-01, 31.10.2026): раздел живёт в profile/payments.tsx, карта
// сохраняется при оплате через платёжную систему.

// fd-payment-03: баланс покупателя — текущий остаток, история операций (леджер)
// и пополнение через существующий платёжный контур (mock-подтверждение).
export function BalancePanel({
  balance,
  ledger,
  onChanged,
  loading
}: {
  balance: BuyerBalance | null;
  ledger: LedgerEntry[];
  onChanged: () => Promise<void>;
  loading: boolean;
}) {
  const {
    t
  } = useTranslation();
  const fmt = useFormatPrice();
  const [topupOpen, setTopupOpen] = useState(false);
  const [amount, setAmount] = useState("1000");
  const [busy, setBusy] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Возврат со страницы Робокассы (?from=robokassa&topup=<id>): исход
  // приносит ResultURL на шлюз, здесь только спрашиваем его — несколько раз,
  // пока уведомление в пути. Параметрам возврата на слово не верим.
  const returnedTopup = searchParams.get("from") === "robokassa" ? searchParams.get("topup") : null;
  // biome-ignore lint/correctness/useExhaustiveDependencies: запускается один раз на возврате
  useEffect(() => {
    if (!returnedTopup) return;
    let cancelled = false;
    void (async () => {
      for (let attempt = 0; attempt < 6 && !cancelled; attempt += 1) {
        try {
          const res = await api.buyer.confirmTopup(returnedTopup);
          if (res.topup.status === "paid") {
            toast.success(t("profile.topupSuccess"));
            await onChanged();
            break;
          }
          if (res.topup.status === "declined") {
            toast.error(t("profile.topupDeclined"));
            break;
          }
        } catch {
          break;
        }
        if (attempt === 5) toast.message(t("profile.topupPending"));
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
      if (!cancelled) {
        const next = new URLSearchParams(searchParams);
        next.delete("from");
        next.delete("topup");
        setSearchParams(next, {
          replace: true
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [returnedTopup]);
  const submitTopup = async () => {
    const value = Number(amount.replace(",", "."));
    if (!Number.isFinite(value) || value <= 0) {
      toast.error(t("profile.topupInvalid"));
      return;
    }
    setBusy(true);
    try {
      const topup = await api.buyer.topupBalance(value);
      const cfg = await api.payments.config();
      // Робокасса — редирект-провайдер: оплата на её странице, вернёмся
      // сюда же (?from=robokassa&topup=…), зачисление подтвердит шлюз.
      if (cfg.mode === "redirect") {
        const {
          url
        } = await api.buyer.topupPaymentUrl(topup.id);
        window.location.assign(url);
        return;
      }
      if (cfg.provider === "cloudpayments") {
        // Виджет пополнения: тот же контур, что и у заказа (InvoiceId = id
        // пополнения). После оплаты подтверждаем на сервере.
        const cp = await import("@/lib/cloudpayments").then(m => m.loadCloudPayments());
        await new Promise<void>(resolve_0 => {
          new cp.CloudPayments().pay("charge", {
            publicId: cfg.publicId ?? "",
            description: t("profile.topupDescription"),
            amount: topup.amount,
            currency: cfg.currency,
            invoiceId: topup.id,
            skin: "mini"
          }, {
            onSuccess: () => resolve_0(),
            onFail: () => resolve_0()
          });
        });
      }
      await api.buyer.confirmTopup(topup.id);
      toast.success(t("profile.topupSuccess"));
      setTopupOpen(false);
      await onChanged();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("common.error"));
    } finally {
      setBusy(false);
    }
  };
  return <Panel title={t("profile.balance")} hint={t("profile.balanceHint")} loading={loading}>
			<div className="flex flex-col gap-5">
				<section className="flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-card p-5 shadow-block">
					<div className="flex items-center gap-3">
						<span className="grid size-12 shrink-0 place-items-center rounded-full bg-osmix-tangelo/10 text-osmix-tangelo"><Wallet className="size-6" /></span>
						<div>
							<p className="type-small text-osmix-icon">{t("profile.currentBalance")}</p>
							<p className="type-h2 text-osmix-text-1" data-testid="balance-amount">{fmt(balance?.amount ?? 0)}</p>
						</div>
					</div>
					<Button variant="osmix-filled" size="pill" data-testid="topup-open" onClick={() => setTopupOpen(true)}>{t("profile.topup")}</Button>
				</section>

				<section className="flex flex-col gap-2">
					<h3 className="type-h4 text-osmix-text-1">{t("profile.ledgerTitle")}</h3>
					{ledger.length === 0 ? <EmptyState icon={Wallet} title={t("profile.noLedger")} hint={t("profile.noLedgerHint")} /> : <ul className="flex flex-col divide-y divide-osmix-gray-300 rounded-2xl bg-card px-4 shadow-block">
							{ledger.map(entry => <li key={entry.id} className="flex items-center gap-3 py-3">
									<span className={cn("grid size-9 shrink-0 place-items-center rounded-full", entry.delta >= 0 ? "bg-status-published/10 text-status-published" : "bg-osmix-gray-100 text-osmix-icon")}>
										{entry.delta >= 0 ? <ArrowDownLeft className="size-4" /> : <ArrowUpRight className="size-4" />}
									</span>
									<div className="min-w-0 flex-1">
										<p className="type-body2-bold text-osmix-text-1">{entry.comment || t(`profile.ledgerKind.${entry.kind}`)}</p>
										<p className="type-small text-osmix-icon">{new Date(entry.createdAt).toLocaleString("ru-RU")}</p>
									</div>
									<div className="shrink-0 text-right">
										<p className={cn("type-body2-bold", entry.delta >= 0 ? "text-status-published" : "text-osmix-text-1")}>{entry.delta >= 0 ? "+" : "−"}{fmt(Math.abs(entry.delta))}</p>
										<p className="type-small text-osmix-icon">{fmt(entry.balanceAfter)}</p>
									</div>
								</li>)}
						</ul>}
				</section>
			</div>

			<Dialog open={topupOpen} onOpenChange={setTopupOpen}>
				<DialogContent className="rounded-2xl sm:max-w-md">
					<DialogHeader>
						<DialogTitle>{t("profile.topup")}</DialogTitle>
						<DialogDescription>{t("profile.topupHint")}</DialogDescription>
					</DialogHeader>
					<label className="flex flex-col gap-2 type-body2-bold">
						{t("profile.topupAmount")}
						<Input data-testid="topup-amount" inputMode="decimal" value={amount} onChange={e_0 => setAmount(e_0.target.value)} />
					</label>
					<DialogFooter>
						<Button type="button" variant="osmix-outline" size="pill" onClick={() => setTopupOpen(false)}>{t("common.cancel")}</Button>
						<Button type="button" variant="osmix-filled" size="pill" data-testid="topup-submit" disabled={busy} onClick={() => void submitTopup()}>
							{busy ? t("checkout.processing") : t("profile.topupConfirm")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</Panel>;
}

// fd-payment-02: реквизиты ЮЛ покупателя для оплаты по счёту (CRUD).
export function BillingPanel(t0) {
  const $ = _c(15);
  const {
    billing,
    onChanged,
    loading
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== t) {
    t1 = t("profile.billing");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t("profile.billingHint");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== onChanged || $[5] !== t) {
    t3 = async () => {
      toast.success(t("profile.billingSaved"));
      await onChanged();
    };
    $[4] = onChanged;
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== billing || $[8] !== t3) {
    t4 = <BillingProfileForm profile={billing} onSaved={t3} />;
    $[7] = billing;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== loading || $[11] !== t1 || $[12] !== t2 || $[13] !== t4) {
    t5 = <Panel title={t1} hint={t2} loading={loading}>{t4}</Panel>;
    $[10] = loading;
    $[11] = t1;
    $[12] = t2;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}
export function SessionsPanel(t0) {
  const $ = _c(21);
  const {
    sessions,
    onChanged,
    loading
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== onChanged || $[1] !== t) {
    t1 = async session => {
      if (session.current) {
        return;
      }
      ;
      try {
        await api.buyer.revokeSession(session.id);
        await onChanged();
      } catch (t2) {
        const e = t2;
        toast.error(e instanceof Error ? e.message : t("common.error"));
      }
    };
    $[0] = onChanged;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const revoke = t1;
  let t2;
  if ($[3] !== t) {
    t2 = t("profile.sessions");
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t) {
    t3 = t("profile.sessionsHint");
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== revoke || $[8] !== sessions || $[9] !== t) {
    let t5;
    if ($[11] !== revoke || $[12] !== t) {
      t5 = session_0 => <article key={session_0.id} className="flex items-center justify-between gap-3 rounded-2xl bg-card p-4 shadow-block"><div className="flex items-center gap-3"><Smartphone className="size-5 text-osmix-icon" /><div><p className="type-body1-bold">{session_0.deviceName || t("profile.unknownDevice")}</p><p className="type-small text-osmix-icon">{new Date(session_0.createdAt).toLocaleString()}</p></div></div>{session_0.current ? <span data-testid="session-current" className="shrink-0 rounded-full bg-status-published/10 px-3 py-1 type-small text-status-published">{t("profile.current")}</span> : <Button variant="osmix-outline" size="pill-sm" onClick={() => void revoke(session_0)}>{t("profile.revoke")}</Button>}</article>;
      $[11] = revoke;
      $[12] = t;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    t4 = sessions.map(t5);
    $[7] = revoke;
    $[8] = sessions;
    $[9] = t;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[14] !== t4) {
    t5 = <div className="grid gap-3">{t4}</div>;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== loading || $[17] !== t2 || $[18] !== t3 || $[19] !== t5) {
    t6 = <Panel title={t2} hint={t3} loading={loading}>{t5}</Panel>;
    $[16] = loading;
    $[17] = t2;
    $[18] = t3;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  return t6;
}
export function OrderUnavailable() {
  const $ = _c(12);
  const {
    t
  } = useTranslation();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <MapPin className="size-8 text-osmix-icon" aria-hidden="true" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== t) {
    t1 = t("profile.orderUnavailable");
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <h3 className="type-h4">{t1}</h3>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t) {
    t3 = t("profile.orderUnavailableHint");
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = <p className="mt-1 type-body2 text-osmix-text-2">{t3}</p>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t2 || $[10] !== t4) {
    t5 = <div className="flex min-h-64 flex-col items-center justify-center gap-3 rounded-2xl bg-card p-6 text-center shadow-block">{t0}<div>{t2}{t4}</div></div>;
    $[9] = t2;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}

// fd-payment-02: счёт по заказу «оплата по счёту». Загружается по запросу и
// рендерится тем же компонентом, что даёт печать/сохранение в PDF.
export function OrderInvoiceSection(t0) {
  const $ = _c(34);
  const {
    order
  } = t0;
  const {
    t
  } = useTranslation();
  const [open, setOpen] = useState(false);
  let t1;
  let t2;
  if ($[0] !== order.id) {
    t1 = ["buyer", "invoice", order.id];
    t2 = () => api.buyer.getOrderInvoice(order.id);
    $[0] = order.id;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] !== open || $[4] !== t1 || $[5] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2,
      enabled: open,
      retry: false
    };
    $[3] = open;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const invoice = useQuery(t3);
  let t4;
  if ($[7] !== t) {
    t4 = t("invoice.title");
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = <h3 className="type-h4 text-osmix-text-1">{t4}</h3>;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t) {
    t6 = t("invoice.orderHint");
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t6) {
    t7 = <p className="type-body2 text-osmix-text-2">{t6}</p>;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t5 || $[16] !== t7) {
    t8 = <div>{t5}{t7}</div>;
    $[15] = t5;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => setOpen(_temp);
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== open || $[20] !== t) {
    t10 = open ? t("invoice.hide") : t("invoice.view");
    $[19] = open;
    $[20] = t;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10) {
    t11 = <Button type="button" variant="osmix-filled" size="pill" data-testid="view-invoice" onClick={t9}>{t10}</Button>;
    $[22] = t10;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11 || $[25] !== t8) {
    t12 = <div className="flex flex-wrap items-center justify-between gap-2">{t8}{t11}</div>;
    $[24] = t11;
    $[25] = t8;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== invoice || $[28] !== open || $[29] !== t) {
    t13 = open && (invoice.isPending ? <Skeleton className="h-64 rounded-2xl" /> : invoice.isError ? <p className="type-body2 text-destructive">{invoice.error instanceof Error ? invoice.error.message : t("common.error")}</p> : invoice.data ? <InvoiceView invoice={invoice.data} /> : null);
    $[27] = invoice;
    $[28] = open;
    $[29] = t;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t12 || $[32] !== t13) {
    t14 = <section className="flex flex-col gap-3 rounded-2xl bg-card p-5 shadow-block">{t12}{t13}</section>;
    $[31] = t12;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  return t14;
}

// Оценка купленного товара после доставки (fd-review-01). Отзыв пишется к
// конкретному товару из заказа — он же попадает в публичную ленту карточки.
// Возвраты — отдельная секция ниже, здесь только отзыв.
function _temp(v) {
  return !v;
}
export function OrderReviewSection({
  order,
  onDone
}: {
  order: Order;
  onDone: () => void;
}) {
  const {
    t
  } = useTranslation();
  const items = order.groups.flatMap(group => group.items);
  const canReview = order.status === "delivered" || order.status === "completed";
  const [productId, setProductId] = useState(items[0]?.productId ?? "");
  const [rating, setRating] = useState(0);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  if (!canReview) return null;

  // Отзыв в модели заказа один. Если уже оставлен — показываем его.
  if (order.review) {
    return <section className="flex flex-col gap-3 rounded-2xl bg-card p-5 shadow-block">
				<h3 className="type-h4">{t("reviewForm.sectionTitle")}</h3>
				<div className="flex flex-col gap-2 rounded-xl border border-osmix-gray-200 p-4">
					<StarRatingInput value={order.review.rating} onChange={() => {}} readOnly />
					{order.review.text && <p className="type-body2 text-osmix-text-1">{order.review.text}</p>}
					<span className="type-small text-osmix-icon">{t("reviewForm.submittedOn", {
            date: new Date(order.review.createdAt).toLocaleDateString()
          })}</span>
				</div>
			</section>;
  }
  const submit = async () => {
    if (rating < 1) return;
    setSubmitting(true);
    try {
      await api.buyer.reviewOrder(order.id, {
        rating,
        text: text.trim() || undefined,
        productId: productId || undefined
      });
      toast.success(t("reviewForm.submitted"));
      setRating(0);
      setText("");
      onDone();
    } catch (cause) {
      toast.error(cause instanceof Error ? cause.message : t("reviewForm.submitError"));
    } finally {
      setSubmitting(false);
    }
  };
  return <section className="flex flex-col gap-3 rounded-2xl bg-card p-5 shadow-block">
			<div>
				<h3 className="type-h4">{t("reviewForm.sectionTitle")}</h3>
				<p className="mt-1 type-body2 text-osmix-text-2">{t("reviewForm.sectionHint")}</p>
			</div>
			{items.length > 1 && <div className="flex flex-col gap-2">
					<span className="type-body2-bold text-osmix-text-1">{t("reviewForm.productLabel")}</span>
					<div className="flex flex-wrap gap-2">
						{items.map(item => <button key={`${item.productId}-${item.variantId ?? item.size}`} type="button" onClick={() => setProductId(item.productId)} className={cn("rounded-full border px-4 py-2 type-body2 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40", productId === item.productId ? "border-osmix-tangelo bg-osmix-tangelo text-white" : "border-osmix-gray-300 text-osmix-text-1 hover:border-osmix-text-2")}>
								{item.name}
							</button>)}
					</div>
				</div>}
			<div className="flex flex-col gap-2">
				<span className="type-body2-bold text-osmix-text-1">{t("reviewForm.ratingLabel")}</span>
				<StarRatingInput value={rating} onChange={setRating} />
			</div>
			<Textarea value={text} onChange={event => setText(event.target.value)} placeholder={t("reviewForm.textPlaceholder")} maxLength={2000} rows={4} />
			<Button variant="osmix-filled" size="pill" className="self-start" disabled={submitting || rating < 1} onClick={() => void submit()}>
				{submitting ? t("reviewForm.submitting") : t("reviewForm.submit")}
			</Button>
		</section>;
}
export function StarRatingInput(t0) {
  const $ = _c(9);
  const {
    value,
    onChange,
    readOnly: t1
  } = t0;
  const readOnly = t1 === undefined ? false : t1;
  const [hover, setHover] = useState(0);
  const shown = hover || value;
  const t2 = readOnly ? undefined : "radiogroup";
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [1, 2, 3, 4, 5];
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] !== onChange || $[2] !== readOnly || $[3] !== shown || $[4] !== value) {
    t4 = t3.map(star => <button key={star} type="button" disabled={readOnly} aria-label={String(star)} aria-pressed={value === star} onMouseEnter={() => !readOnly && setHover(star)} onMouseLeave={() => !readOnly && setHover(0)} onClick={() => !readOnly && onChange(star)} className={cn("rounded-sm outline-none transition-transform focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40", !readOnly && "hover:scale-110")}><Star className={cn("size-7", star <= shown ? "fill-osmix-yellow text-osmix-yellow" : "text-osmix-gray-300")} /></button>);
    $[1] = onChange;
    $[2] = readOnly;
    $[3] = shown;
    $[4] = value;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t2 || $[7] !== t4) {
    t5 = <div className="flex items-center gap-1" role={t2}>{t4}</div>;
    $[6] = t2;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}

// Возврат/спор по доставленному заказу (fd-order-19). Кнопка инициирует
// заявку, ниже показывается статус уже поданных заявок покупателю.
export function OrderReturnSection({
  order,
  onDone
}: {
  order: Order;
  onDone: () => void;
}) {
  const {
    t
  } = useTranslation();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const requests = order.returnRequests ?? [];
  const canRequest = order.status === "delivered" || order.status === "completed";
  const submit = async (values: {
    kind: "return" | "dispute";
    reason: string;
  }) => {
    setSubmitting(true);
    try {
      await api.buyer.requestOrderReturn(order.id, values);
      toast.success(t("returns.submitted"));
      setOpen(false);
      onDone();
    } catch (cause) {
      toast.error(cause instanceof Error ? cause.message : t("returns.submitError"));
    } finally {
      setSubmitting(false);
    }
  };
  if (!canRequest && requests.length === 0) return null;
  return <section className="flex flex-col gap-3 rounded-2xl bg-card p-5 shadow-block">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<h3 className="type-h4">{t("returns.sectionTitle")}</h3>
				{canRequest && <Button variant="osmix-filled" size="pill-sm" onClick={() => setOpen(true)}>
						{t("returns.requestButton")}
					</Button>}
			</div>
			{requests.length > 0 ? <ul className="flex flex-col gap-2">
					{requests.map(request => <li key={request.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-osmix-gray-200 p-3">
							<div className="min-w-0">
								<p className="type-body2-bold text-osmix-text-1">{t(`returns.kind.${request.kind}`)}</p>
								<p className="type-small text-osmix-text-2">{new Date(request.createdAt).toLocaleDateString()}</p>
							</div>
							<span className="self-start rounded-full bg-status-moderation/10 px-3 py-1 type-small font-bold text-status-moderation">
								{t([`returns.status.${request.status}`, request.status])}
							</span>
						</li>)}
				</ul> : <p className="type-body2 text-osmix-text-2">{t("returns.sectionHint")}</p>}
			<ReturnRequestDialog open={open} onOpenChange={setOpen} onSubmit={submit} submitting={submitting} />
		</section>;
}
export function OrderCard(t0) {
  const $ = _c(99);
  const {
    order
  } = t0;
  const {
    t,
    i18n
  } = useTranslation();
  const fmt = useFormatPrice();
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[0] !== fmt || $[1] !== i18n.language || $[2] !== order.address.city || $[3] !== order.address.country || $[4] !== order.address.postalCode || $[5] !== order.address.street || $[6] !== order.createdAt || $[7] !== order.groups || $[8] !== order.id || $[9] !== order.itemCount || $[10] !== order.number || $[11] !== order.status || $[12] !== order.total || $[13] !== t) {
    const firstItems = order.groups.flatMap(_temp2).slice(0, 3);
    t6 = "rounded-2xl bg-card p-5 shadow-block";
    let t8;
    if ($[21] !== order.createdAt) {
      t8 = new Date(order.createdAt).toLocaleDateString();
      $[21] = order.createdAt;
      $[22] = t8;
    } else {
      t8 = $[22];
    }
    let t9;
    if ($[23] !== t8) {
      t9 = <p className="type-small text-osmix-icon">{t8}</p>;
      $[23] = t8;
      $[24] = t9;
    } else {
      t9 = $[24];
    }
    let t10;
    if ($[25] !== order.id || $[26] !== order.number || $[27] !== t) {
      t10 = t("profile.orderNumber", {
        id: orderNumber(order.id, order.number)
      });
      $[25] = order.id;
      $[26] = order.number;
      $[27] = t;
      $[28] = t10;
    } else {
      t10 = $[28];
    }
    let t11;
    if ($[29] !== t10) {
      t11 = <h3 className="type-h4">{t10}</h3>;
      $[29] = t10;
      $[30] = t11;
    } else {
      t11 = $[30];
    }
    let t12;
    if ($[31] !== order.itemCount || $[32] !== t) {
      t12 = t("cart.items", {
        count: order.itemCount
      });
      $[31] = order.itemCount;
      $[32] = t;
      $[33] = t12;
    } else {
      t12 = $[33];
    }
    let t13;
    if ($[34] !== t12) {
      t13 = <p className="type-body2 text-osmix-text-2">{t12}</p>;
      $[34] = t12;
      $[35] = t13;
    } else {
      t13 = $[35];
    }
    let t14;
    if ($[36] !== t11 || $[37] !== t13 || $[38] !== t9) {
      t14 = <div>{t9}{t11}{t13}</div>;
      $[36] = t11;
      $[37] = t13;
      $[38] = t9;
      $[39] = t14;
    } else {
      t14 = $[39];
    }
    let t15;
    if ($[40] !== i18n.language || $[41] !== order.status) {
      t15 = orderStatusLabel(order.status, i18n.language);
      $[40] = i18n.language;
      $[41] = order.status;
      $[42] = t15;
    } else {
      t15 = $[42];
    }
    let t16;
    if ($[43] !== t15) {
      t16 = <span className="rounded-full bg-status-published/10 px-3 py-1 type-small text-status-published">{t15}</span>;
      $[43] = t15;
      $[44] = t16;
    } else {
      t16 = $[44];
    }
    let t17;
    if ($[45] !== fmt || $[46] !== order.total) {
      t17 = fmt(order.total);
      $[45] = fmt;
      $[46] = order.total;
      $[47] = t17;
    } else {
      t17 = $[47];
    }
    let t18;
    if ($[48] !== t17) {
      t18 = <span className="type-h4">{t17}</span>;
      $[48] = t17;
      $[49] = t18;
    } else {
      t18 = $[49];
    }
    let t19;
    if ($[50] !== t16 || $[51] !== t18) {
      t19 = <div className="flex items-center gap-3">{t16}{t18}</div>;
      $[50] = t16;
      $[51] = t18;
      $[52] = t19;
    } else {
      t19 = $[52];
    }
    if ($[53] !== t14 || $[54] !== t19) {
      t7 = <div className="flex flex-col gap-3 border-b border-osmix-gray-300 pb-4 md:flex-row md:items-start md:justify-between">{t14}{t19}</div>;
      $[53] = t14;
      $[54] = t19;
      $[55] = t7;
    } else {
      t7 = $[55];
    }
    t4 = "flex flex-col gap-4 pt-4";
    let t20;
    if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = <MapPin className="size-4 text-osmix-icon" />;
      $[56] = t20;
    } else {
      t20 = $[56];
    }
    let t21;
    if ($[57] !== t) {
      t21 = t("profile.deliveryAddress");
      $[57] = t;
      $[58] = t21;
    } else {
      t21 = $[58];
    }
    let t22;
    if ($[59] !== t21) {
      t22 = <div className="mb-1 flex items-center gap-2 type-body2-bold text-osmix-text-1">{t20}{t21}</div>;
      $[59] = t21;
      $[60] = t22;
    } else {
      t22 = $[60];
    }
    let t23;
    if ($[61] !== order.address.city || $[62] !== order.address.country) {
      t23 = <p>{order.address.country}, {order.address.city}</p>;
      $[61] = order.address.city;
      $[62] = order.address.country;
      $[63] = t23;
    } else {
      t23 = $[63];
    }
    let t24;
    if ($[64] !== order.address.street) {
      t24 = <p>{order.address.street}</p>;
      $[64] = order.address.street;
      $[65] = t24;
    } else {
      t24 = $[65];
    }
    let t25;
    if ($[66] !== t) {
      t25 = t("checkout.postalCode");
      $[66] = t;
      $[67] = t25;
    } else {
      t25 = $[67];
    }
    let t26;
    if ($[68] !== order.address.postalCode || $[69] !== t25) {
      t26 = <p>{t25}: {order.address.postalCode}</p>;
      $[68] = order.address.postalCode;
      $[69] = t25;
      $[70] = t26;
    } else {
      t26 = $[70];
    }
    if ($[71] !== t22 || $[72] !== t23 || $[73] !== t24 || $[74] !== t26) {
      t5 = <div className="rounded-xl bg-white/70 p-3 type-body2 text-osmix-text-2">{t22}{t23}{t24}{t26}</div>;
      $[71] = t22;
      $[72] = t23;
      $[73] = t24;
      $[74] = t26;
      $[75] = t5;
    } else {
      t5 = $[75];
    }
    t3 = "flex flex-wrap items-center justify-between gap-4";
    t1 = "flex -space-x-2";
    let t27;
    if ($[76] !== order.id) {
      t27 = it => <Link key={`${order.id}-${it.productId}-${it.size}`} to={`/product/${it.productId}`} className="size-14 overflow-hidden rounded-xl border-2 border-white bg-osmix-gray-100 outline-none transition-colors hover:border-osmix-text-1 focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40">{it.photo ? <img src={it.photo} alt={it.name} className="size-full object-cover" /> : null}</Link>;
      $[76] = order.id;
      $[77] = t27;
    } else {
      t27 = $[77];
    }
    t2 = firstItems.map(t27);
    $[0] = fmt;
    $[1] = i18n.language;
    $[2] = order.address.city;
    $[3] = order.address.country;
    $[4] = order.address.postalCode;
    $[5] = order.address.street;
    $[6] = order.createdAt;
    $[7] = order.groups;
    $[8] = order.id;
    $[9] = order.itemCount;
    $[10] = order.number;
    $[11] = order.status;
    $[12] = order.total;
    $[13] = t;
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    t1 = $[14];
    t2 = $[15];
    t3 = $[16];
    t4 = $[17];
    t5 = $[18];
    t6 = $[19];
    t7 = $[20];
  }
  let t8;
  if ($[78] !== t1 || $[79] !== t2) {
    t8 = <div className={t1}>{t2}</div>;
    $[78] = t1;
    $[79] = t2;
    $[80] = t8;
  } else {
    t8 = $[80];
  }
  const t9 = `/profile/orders/${encodeURIComponent(order.id)}`;
  let t10;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Eye className="size-4" aria-hidden="true" />;
    $[81] = t10;
  } else {
    t10 = $[81];
  }
  let t11;
  if ($[82] !== t) {
    t11 = t("profile.viewOrder");
    $[82] = t;
    $[83] = t11;
  } else {
    t11 = $[83];
  }
  let t12;
  if ($[84] !== t11 || $[85] !== t9) {
    t12 = <Button variant="osmix-outline" size="pill-sm" asChild={true}><Link to={t9}>{t10}{t11}</Link></Button>;
    $[84] = t11;
    $[85] = t9;
    $[86] = t12;
  } else {
    t12 = $[86];
  }
  let t13;
  if ($[87] !== t12 || $[88] !== t3 || $[89] !== t8) {
    t13 = <div className={t3}>{t8}{t12}</div>;
    $[87] = t12;
    $[88] = t3;
    $[89] = t8;
    $[90] = t13;
  } else {
    t13 = $[90];
  }
  let t14;
  if ($[91] !== t13 || $[92] !== t4 || $[93] !== t5) {
    t14 = <div className={t4}>{t5}{t13}</div>;
    $[91] = t13;
    $[92] = t4;
    $[93] = t5;
    $[94] = t14;
  } else {
    t14 = $[94];
  }
  let t15;
  if ($[95] !== t14 || $[96] !== t6 || $[97] !== t7) {
    t15 = <article className={t6}>{t7}{t14}</article>;
    $[95] = t14;
    $[96] = t6;
    $[97] = t7;
    $[98] = t15;
  } else {
    t15 = $[98];
  }
  return t15;
}
function _temp2(g) {
  return g.items;
}